import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import FormikProvider from '../../shared/forms/formikProvider.component';
import Modal from '../../shared/modals/modal.component';
import Form from '../../shared/forms/form.component';

import schema from './legalEntityForm.schema';

import { LegalEntityPropTypes } from '../../shared/propTypes/legalEntities';

import { PeriodPropTypes } from '../shared/propTypes';
import { OptionsPropType } from '../../shared/propTypes/common';

import {
  fetchInitialDataForLegalEntityModal,
} from './store/actions';
import {
  enabledFlagOptionsSelector,
  usTaxTypeOptionsSelector,
  localTaxTypeOptionsSelector,
  countryCodeOptionsSelector,
  branchOptionsSelector,
  sharedSelectors,
  isFetchingInitialDataForModalSelector,
  sourceOptionsSelector,
  legalEntityOptionsSelector,
} from './store/selectors';

const ModalTitles = {
  ADD: 'Add Legal Entity',
  EDIT: 'Edit Legal Entity',
};

const getDecimal = value => parseFloat(value) ? (parseFloat(value) / 100).toFixed(6) : '';

class AddLegalEntityModal extends PureComponent {
  static propTypes = {
    fetchInitialDataForLegalEntityModal: PropTypes.func.isRequired,
    hideModal: PropTypes.func.isRequired,
    saveEntity: PropTypes.func.isRequired,
    enabledFlagOptions: OptionsPropType,
    usTaxTypeOptions: OptionsPropType,
    localTaxTypeOptions: OptionsPropType,
    countryCodeOptions: OptionsPropType,
    branchOptions: OptionsPropType,
    sourceOptions: OptionsPropType,
    period: PeriodPropTypes.isRequired,
    isSavingInProgress: PropTypes.bool.isRequired,
    isLoadingInitialData: PropTypes.bool.isRequired,
    entityToEdit: LegalEntityPropTypes,
    openForEdit: PropTypes.bool,
    isVisible: PropTypes.bool,
    legalEntitiesOptions: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })),
  };

  state = {
    addAnother: false,
  };

  componentDidMount() {
    const {
      fetchInitialDataForLegalEntityModal,
      period,
    } = this.props;

    fetchInitialDataForLegalEntityModal(period);
  }

  save = (formData, { setSubmitting, resetForm }) => {
    const { period, saveEntity, openForEdit } = this.props;
    const { addAnother } = this.state;

    this.setState({ addAnother: false }, () => {
      saveEntity({
        ...formData,
        minorityPercentage: getDecimal(formData.minorityPercentage) || '0',
        ownership1: getDecimal(formData.ownership1),
        ownership2: getDecimal(formData.ownership2),
        ownership3: getDecimal(formData.ownership3),
        ownership4: getDecimal(formData.ownership4),
        ownership5: getDecimal(formData.ownership5),
        ownership6: getDecimal(formData.ownership6),
        ownership7: getDecimal(formData.ownership7),
        ownership8: getDecimal(formData.ownership8),
        ownership9: getDecimal(formData.ownership9),
        ownership10: getDecimal(formData.ownership10),
        periodName: period.name,
      }, openForEdit, {
        params: { periodName: period.name, legalEntity: formData.legalEntity },
        callback: resetForm,
        keepOpenAfterSaving: addAnother,
      });
      setSubmitting(false);
    });
  };

  renderModal = ({ formik, schema }) => {
    const {
      openForEdit,
      enabledFlagOptions,
      usTaxTypeOptions,
      localTaxTypeOptions,
      branchOptions,
      countryCodeOptions,
      hideModal,
      isVisible,
      isSavingInProgress,
      isLoadingInitialData,
      sourceOptions,
    } = this.props;

    const submit = () => {
      this.setState({
        addAnother: false,
      }, () => formik.handleSubmit());
    };

    const submitAndAddAnotherAction = () => {
      this.setState({
        addAnother: true,
      }, () => formik.handleSubmit());
    };

    const reset = () => {
      hideModal();
      formik.resetForm();
    };

    return (
      <Modal
        className="add-legal-entity-modal"
        title={openForEdit ? ModalTitles.EDIT : ModalTitles.ADD}
        closeAction={reset} // eslint-disable-line react/jsx-no-bind
        dismissText="Cancel"
        dismissAction={reset} // eslint-disable-line react/jsx-no-bind
        submitText="Save"
        submitAction={submit} // eslint-disable-line react/jsx-no-bind
        isSubmitting={formik.isSubmitting || isSavingInProgress}
        submitAndAddAnotherAction={!openForEdit ? submitAndAddAnotherAction : null}
        submitAndAddAnotherText="Save and Add Another"
        isLoading={isLoadingInitialData}
        isVisible={isVisible}
      >
        <Form
          formik={formik}
          schema={schema}
          openForEdit={openForEdit}
          formContext={{
            enabledFlagOptions,
            usTaxTypeOptions,
            localTaxTypeOptions,
            branchOptions,
            countryCodeOptions,
            sourceOptions,
          }}
        />
      </Modal>
    );
  };

  render() {
    const {
      entityToEdit,
      legalEntitiesOptions,
      openForEdit,
    } = this.props;

    const formattedEntityToEdit = entityToEdit
      ? {
        ...entityToEdit,
        minorityPercentage: Number(entityToEdit.minorityPercentage) * 100,
        ownership1: Number(entityToEdit.ownership1) * 100,
        ownership2: Number(entityToEdit.ownership2) * 100,
        ownership3: Number(entityToEdit.ownership3) * 100,
        ownership4: Number(entityToEdit.ownership4) * 100,
        ownership5: Number(entityToEdit.ownership5) * 100,
        ownership6: Number(entityToEdit.ownership6) * 100,
        ownership7: Number(entityToEdit.ownership7) * 100,
        ownership8: Number(entityToEdit.ownership8) * 100,
        ownership9: Number(entityToEdit.ownership9) * 100,
        ownership10: Number(entityToEdit.ownership10) * 100,
      }
      : null;

    return (
      <FormikProvider
        schema={schema}
        entityToEdit={formattedEntityToEdit}
        onSubmit={this.save}
        render={this.renderModal}
        validationContext={{
          legalEntitiesOptions,
          openForEdit,
        }}
      />
    );
  }
}

function mapStateToProps(state) {
  return {
    enabledFlagOptions: enabledFlagOptionsSelector(state),
    usTaxTypeOptions: usTaxTypeOptionsSelector(state),
    localTaxTypeOptions: localTaxTypeOptionsSelector(state),
    branchOptions: branchOptionsSelector(state),
    sourceOptions: sourceOptionsSelector(state),
    countryCodeOptions: countryCodeOptionsSelector(state),
    isSavingInProgress: sharedSelectors.savingEntitySelector(state),
    isLoadingInitialData: isFetchingInitialDataForModalSelector(state),
    legalEntitiesOptions: legalEntityOptionsSelector(state),
  };
}

export default connect(
  mapStateToProps,
  {
    fetchInitialDataForLegalEntityModal,
  }
)(AddLegalEntityModal);
